import { Trans, useLingui } from '@lingui/react/macro'
import dayjs from 'dayjs'
import { Button } from 'designSystem/components/Button'
import { TimeRange } from 'features/systemLens'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { SentinelCalculatingDots } from 'features/timeSeries/components/SentinelCalculatingIndicator'
import { TimeSeriesLoadingDots } from 'features/timeSeries/components/timeSeriesScatterplot/TimeSeriesLoadingDots'
import { useSentinelScoringActive } from 'features/timeSeries/hooks/useSentinelOverlayMap'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { TimeSeriesService } from 'features/timeSeries/timeSeriesService'
import {
  canNavigateForward,
  getNavigatedTimeRange,
  navigateTimeRange,
} from 'features/timeSeries/utils/activeTimeRangeHelpers'
import {
  formatSampleCadence,
  isSpanAtMaxZoom,
} from 'features/timeSeries/utils/maxZoom'
import { estimateSampleIntervalMs } from 'features/timeSeries/utils/sampleCadence'
import {
  ChevronLeft,
  ChevronRight,
  PanelRight,
  RotateCcw,
  SlidersHorizontal,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { ActiveTimeRangeSectionWithPopover } from './ActiveTimeRangeSectionWithPopover'

const ContainerQueryWrapper = styled.div`
  container-type: inline-size;
`

const ActiveRangeDisplayContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  position: relative;
  column-gap: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  padding: 0.0625rem 0.5rem;

  div[data-role='right-actions'] {
    position: absolute;
    right: 0.25rem;
    display: flex;
    align-items: center;
  }
`

const TimeDisplayRow = styled.div`
  display: flex;
  justify-content: center;

  @container (max-width: 500px) {
    width: 100%;
  }
`

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

// Out of flow so the centered controls never shift when loading toggles. It
// hugs the far right edge, stepping in only when the right-actions slot is
// occupying that corner.
const LoadingSlot = styled.div<{ $isVisible: boolean; $withActions: boolean }>`
  position: absolute;
  right: ${({ $withActions }) => ($withActions ? '3rem' : '0.5rem')};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.375rem;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
  pointer-events: none;
`

// When the footer runs tight the dots alone carry the state.
const LoadingLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ theme }) => theme.forecast.secondaryText};

  @container (max-width: 760px) {
    display: none;
  }
`

// Informational only — pan/zoom-out/presets all keep working at the floor, so
// this must read as a status, not a disabled control.
const MaxZoomTag = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ theme }) => theme.forecast.secondaryText};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.25rem;
  padding: 0 0.375rem;
  margin-left: 0.25rem;
`

interface ActiveTimeRangeSelectorProps {
  timeRange: TimeRange
  setTimeRange: React.Dispatch<React.SetStateAction<TimeRange>>
  onClickBackward?: () => void
  onClickForward?: () => void
  liveTail?: boolean
  onRangeClick?: () => void
  onSettingsClick?: () => void
  onResetViewport?: () => void
  onPenDrawerClick?: () => void
  isSettingsOpen?: boolean
  isPenDrawerOpen?: boolean
}

export const ActiveTimeRangeSelector: React.FC<
  ActiveTimeRangeSelectorProps
> = ({
  timeRange,
  setTimeRange,
  onClickBackward,
  onClickForward,
  liveTail,
  onRangeClick,
  onSettingsClick,
  onResetViewport,
  onPenDrawerClick,
  isSettingsOpen = false,
  isPenDrawerOpen = false,
}) => {
  const { t } = useLingui()

  const {
    chartData,
    dataLayers,
    isLoading,
    prefetchTimeRange,
    selectedSensors,
    exitLiveTail,
  } = useScatterplotContext()
  const isSentinelScoring = useSentinelScoringActive()
  const [showDateConfig, setShowDateConfig] = useState(false)
  const { fromTime, toTime } = useMemo(() => {
    return { fromTime: dayjs(timeRange.from), toTime: dayjs(timeRange.to) }
  }, [timeRange.from, timeRange.to])
  const hasEnabledSensorLayer = useMemo(
    () => dataLayers.some((layer) => layer.type === 'sensor' && layer.enabled),
    [dataLayers]
  )

  // "Max zoom" status: the committed window sits at the data-derived zoom floor
  // (`clampTimeRange` expands any narrower gesture to it), so zooming in
  // further only re-centers. Same cadence estimate the box-zoom floor uses
  // (`useChartViewportInteractions.minZoomSpanRef`), so the tag and the clamp
  // can never disagree about where the floor is. Gated on data being present:
  // with nothing on screen there is no cadence and no honest claim to make.
  const sampleCadenceMs = useMemo(() => {
    if (chartData.length === 0) return null
    return estimateSampleIntervalMs(
      chartData
        .map((point) => point.timestamp)
        .filter(
          (timestamp): timestamp is number => typeof timestamp === 'number'
        )
    )
  }, [chartData])
  const isAtMaxZoom = useMemo(() => {
    if (chartData.length === 0) return false
    return isSpanAtMaxZoom(
      toTime.valueOf() - fromTime.valueOf(),
      sampleCadenceMs
    )
  }, [chartData.length, fromTime, toTime, sampleCadenceMs])

  // Ensure timeRange always has interval/intervalUnit computed. This must be
  // the SAME interval buildTimeSeriesRequest derives for an interval-less
  // range (same helper, same pen count, same fractional seconds), otherwise
  // backfilling it changes the request signature and refetches the window the
  // initial fetch already loaded.
  useEffect(() => {
    if (!timeRange.interval || !timeRange.intervalUnit) {
      const timeRangeSeconds = (toTime.valueOf() - fromTime.valueOf()) / 1000
      const { interval, interval_unit } =
        TimeSeriesService.getIntervalForPenCount(
          timeRangeSeconds,
          selectedSensors.length
        )
      setTimeRange({
        ...timeRange,
        interval,
        intervalUnit: interval_unit,
      })
    }
  }, [setTimeRange, timeRange, fromTime, toTime, selectedSensors.length])

  useEffect(() => {
    if (
      isLoading ||
      liveTail ||
      selectedSensors.length === 0 ||
      !hasEnabledSensorLayer
    ) {
      return
    }

    // Prefetched ranges bake their interval too, so they must use the same
    // pen count as the fetch a real navigation would issue.
    const navOptions = { penCount: selectedSensors.length }
    const rangesToPrefetch = [
      getNavigatedTimeRange(
        'backward',
        false,
        fromTime,
        toTime,
        dayjs(),
        navOptions
      ),
      ...(canNavigateForward(toTime)
        ? [
            getNavigatedTimeRange(
              'forward',
              false,
              fromTime,
              toTime,
              dayjs(),
              navOptions
            ),
          ]
        : []),
      getNavigatedTimeRange(
        'backward',
        true,
        fromTime,
        toTime,
        dayjs(),
        navOptions
      ),
      getNavigatedTimeRange(
        'forward',
        true,
        fromTime,
        toTime,
        dayjs(),
        navOptions
      ),
    ]

    void Promise.all(
      rangesToPrefetch.map((range) => prefetchTimeRange(range))
    ).catch(() => undefined)
  }, [
    fromTime,
    hasEnabledSensorLayer,
    isLoading,
    liveTail,
    prefetchTimeRange,
    selectedSensors.length,
    toTime,
  ])

  const handleBackwardClick = useCallback(() => {
    exitLiveTail()
    navigateTimeRange('backward', false, fromTime, toTime, setTimeRange, {
      penCount: selectedSensors.length,
    })
    onClickBackward?.()
  }, [
    fromTime,
    toTime,
    setTimeRange,
    onClickBackward,
    exitLiveTail,
    selectedSensors.length,
  ])

  const handleForwardClick = useCallback(() => {
    exitLiveTail()
    navigateTimeRange('forward', false, fromTime, toTime, setTimeRange, {
      penCount: selectedSensors.length,
    })
    onClickForward?.()
  }, [
    fromTime,
    toTime,
    setTimeRange,
    onClickForward,
    exitLiveTail,
    selectedSensors.length,
  ])

  const handleRangeClick = useCallback(() => {
    if (onRangeClick) {
      onRangeClick()
    } else {
      setShowDateConfig(!showDateConfig)
    }
  }, [onRangeClick, showDateConfig])

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    setShowDateConfig(open)
  }, [])

  const handleZoomOutClick = useCallback(() => {
    exitLiveTail()
    navigateTimeRange('backward', true, fromTime, toTime, setTimeRange, {
      penCount: selectedSensors.length,
    })
  }, [fromTime, toTime, setTimeRange, exitLiveTail, selectedSensors.length])

  const handleZoomInClick = useCallback(() => {
    exitLiveTail()
    navigateTimeRange('forward', true, fromTime, toTime, setTimeRange, {
      penCount: selectedSensors.length,
    })
  }, [fromTime, toTime, setTimeRange, exitLiveTail, selectedSensors.length])

  return (
    <>
      <ContainerQueryWrapper>
        <ActiveRangeDisplayContainer>
          <TimeDisplayRow>
            <ActiveTimeRangeSectionWithPopover
              isCustom={timeRange.preset === 'custom' && liveTail !== true}
              onClick={handleRangeClick}
              fromTime={fromTime}
              toTime={toTime}
              timeRange={timeRange}
              onRangeClick={onRangeClick}
              showDateConfig={showDateConfig}
              onOpenChange={handlePopoverOpenChange}
              onClose={() => setShowDateConfig(false)}
            />
          </TimeDisplayRow>

          <ControlsRow>
            {onResetViewport && (
              <ChartTooltip label={t`Reset to saved date`} side='top'>
                <Button
                  onClick={onResetViewport}
                  variant='ghost'
                  size='icon-lg'
                  aria-label={t`Reset to saved date`}>
                  <RotateCcw />
                </Button>
              </ChartTooltip>
            )}

            <ChartTooltip label={t`Previous time period`} side='top'>
              <Button
                onClick={handleBackwardClick}
                variant='ghost'
                size='icon-lg'
                aria-label={t`Previous time period`}>
                <ChevronLeft />
              </Button>
            </ChartTooltip>
            <ChartTooltip label={t`Next time period`} side='top'>
              <Button
                onClick={handleForwardClick}
                variant='ghost'
                size='icon-lg'
                aria-label={t`Next time period`}
                disabled={!canNavigateForward(toTime)}>
                <ChevronRight />
              </Button>
            </ChartTooltip>

            <ChartTooltip label={t`Zoom Out`} side='top'>
              <Button
                variant='ghost'
                size='icon-lg'
                aria-label={t`Zoom Out`}
                onClick={handleZoomOutClick}>
                <ZoomOut />
              </Button>
            </ChartTooltip>
            <ChartTooltip label={t`Zoom In`} side='top'>
              <Button
                variant='ghost'
                size='icon-lg'
                aria-label={t`Zoom In`}
                onClick={handleZoomInClick}>
                <ZoomIn />
              </Button>
            </ChartTooltip>

            {isAtMaxZoom && (
              <ChartTooltip
                label={t`This window is at the data's resolution — about one sample every ${{ value437_93: formatSampleCadence(sampleCadenceMs) }}. Zooming in further won't reveal more detail; panning and zooming out still work.`}
                side='top'>
                <MaxZoomTag role='status' aria-label={t`Max zoom reached`}>
                  <Trans>Max zoom</Trans>
                </MaxZoomTag>
              </ChartTooltip>
            )}

            <LoadingSlot
              $isVisible={isLoading || isSentinelScoring}
              $withActions={Boolean(onSettingsClick || onPenDrawerClick)}
              data-testid='time-series-loading-indicator'>
              {isLoading ? (
                <>
                  <LoadingLabel>
                    <Trans>Loading</Trans>
                  </LoadingLabel>
                  <TimeSeriesLoadingDots />
                </>
              ) : isSentinelScoring ? (
                <>
                  <LoadingLabel>
                    <Trans>Calculating</Trans>
                  </LoadingLabel>
                  <SentinelCalculatingDots />
                </>
              ) : null}
            </LoadingSlot>
          </ControlsRow>

          {(onSettingsClick || onPenDrawerClick) && (
            <div data-role='right-actions'>
              {onSettingsClick && (
                <ChartTooltip label={t`Edit pens`} side='top'>
                  <Button
                    onClick={onSettingsClick}
                    variant='ghost'
                    size='icon-lg'
                    aria-label={t`Edit pens`}
                    aria-pressed={isSettingsOpen}
                    pressed='accent'>
                    <SlidersHorizontal />
                  </Button>
                </ChartTooltip>
              )}
              {onPenDrawerClick && (
                <ChartTooltip label={t`Show tag names`} side='top'>
                  <Button
                    onClick={onPenDrawerClick}
                    variant='ghost'
                    size='icon-lg'
                    aria-label={t`Show tag names`}
                    aria-pressed={isPenDrawerOpen}
                    pressed='accent'>
                    <PanelRight />
                  </Button>
                </ChartTooltip>
              )}
            </div>
          )}
        </ActiveRangeDisplayContainer>
      </ContainerQueryWrapper>
    </>
  )
}
